/**
 * UI Configuration - Zentrale Styling-Konstanten
 * Verwendet Tailwind Custom Colors aus tailwind.config.js
 */

import type { StarRating } from '@/types/rating.types';

// ============================================================================
// STAR RATING STYLES (NEU - Februar 2024)
// ============================================================================

/**
 * Sterne-Level für Farbcodierung
 * - excellent: 5 Sterne (dunkelgrün)
 * - good: 4 Sterne (hellgrün)
 * - medium: 3 Sterne (gelb/amber)
 * - poor: 1-2 Sterne (rot)
 */
export type StarLevel = 'excellent' | 'good' | 'medium' | 'poor' | 'unrated';

/**
 * Styles für Sterne-Bewertungen
 */
export const STAR_STYLES = {
  excellent: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    border: 'border-emerald-500',
    fill: 'fill-emerald-500',
    starColor: 'text-emerald-500',
  },
  good: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-500',
    fill: 'fill-green-500',
    starColor: 'text-green-500',
  },
  medium: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-500',
    fill: 'fill-amber-500',
    starColor: 'text-amber-500',
  },
  poor: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-500',
    fill: 'fill-red-500',
    starColor: 'text-red-500',
  },
  unrated: {
    bg: 'bg-gray-100',
    text: 'text-gray-500',
    border: 'border-gray-300',
    fill: 'fill-gray-300',
    starColor: 'text-gray-300',
  },
} as const;

/**
 * Bestimmt das Sterne-Level basierend auf dem Wert (0-5)
 * 5 = excellent (dunkelgrün)
 * 4 = good (hellgrün)
 * 3 = medium (gelb)
 * 1-2 = poor (rot)
 * 0 = unrated (grau)
 */
export function getStarLevel(stars: number): StarLevel {
  if (stars === 0 || stars === null || stars === undefined) return 'unrated';
  if (stars >= 4.5) return 'excellent';
  if (stars >= 3.5) return 'good';
  if (stars >= 2.5) return 'medium';
  return 'poor';
}

/**
 * Gibt die Tailwind-Klassen für eine Sterne-Bewertung zurück
 */
export function getStarClasses(stars: number): {
  bg: string;
  text: string;
  border: string;
  fill: string;
  starColor: string;
} {
  const level = getStarLevel(stars);
  return STAR_STYLES[level];
}

/**
 * Gibt die Farbe für einen einzelnen Stern zurück
 * Gefüllte Sterne: Je nach Gesamtbewertung
 * Leere Sterne: Grau
 */
export function getStarColor(starIndex: number, rating: StarRating): string {
  if (starIndex < rating) {
    // Gefüllter Stern - Farbe basiert auf Gesamtrating
    return getStarClasses(rating).starColor;
  }
  // Leerer Stern
  return 'text-gray-300';
}

// ============================================================================
// LEGACY STYLES (für Übergangszeit)
// ============================================================================

/**
 * @deprecated Verwende STAR_STYLES für das neue Sterne-System
 * Score Level Styles - für normale 1-10 Scores
 */
export const SCORE_STYLES = {
  good: {
    bg: 'bg-score-good-bg',
    text: 'text-score-good',
    border: 'border-score-good',
    fill: 'fill-score-good',
  },
  medium: {
    bg: 'bg-score-medium-bg',
    text: 'text-score-medium',
    border: 'border-score-medium',
    fill: 'fill-score-medium',
  },
  poor: {
    bg: 'bg-score-poor-bg',
    text: 'text-score-poor',
    border: 'border-score-poor',
    fill: 'fill-score-poor',
  },
} as const;


/**
 * Bestimmt das Score-Level basierend auf dem Sterne-Wert (0-5 Skala)
 * >=4: gut (grün), >=3: mittel (amber), <3: schlecht (rot)
 */
export function getScoreLevel(score: number): 'good' | 'medium' | 'poor' {
  if (score >= 4) return 'good';
  if (score >= 3) return 'medium';
  return 'poor';
}

/**
 * @deprecated Verwende getStarLevel für das neue Sterne-System
 * Bestimmt das Score-Level basierend auf der Schulnote (1.0-6.0)
 * 1-2: gut (grün), 3-4: mittel (amber), 5-6: schlecht (rot)
 */
export function getGradeLevelFromValue(gradeValue: number): 'good' | 'medium' | 'poor' {
  if (gradeValue <= 2.3) return 'good';    // 1+, 1, 1-, 2+, 2, 2-
  if (gradeValue <= 4.3) return 'medium';  // 3+, 3, 3-, 4+, 4, 4-
  return 'poor';                            // 5+, 5, 5-, 6
}

/**
 * @deprecated Verwende getStarClasses für das neue Sterne-System
 * Gibt die Tailwind-Klassen für eine Schulnote zurück
 */
export function getGradeClasses(gradeValue: number): {
  bg: string;
  text: string;
  border: string;
  fill: string;
} {
  const level = getGradeLevelFromValue(gradeValue);
  return SCORE_STYLES[level];
}

/**
 * Gibt die Tailwind-Klassen für einen Score zurück
 * - CherryScore: Schulnoten 0.7-6.0 (niedriger = besser)
 */
export function getScoreClasses(score: number): {
  bg: string;
  text: string;
  border: string;
  fill: string;
} {
  const level = getGradeLevelFromValue(score);
  return SCORE_STYLES[level];
}

/**
 * Touch Target Minimum Size (iOS Guidelines: 44px)
 */
export const TOUCH_TARGET = {
  minSize: 'min-w-11 min-h-11',
  minHeight: 'min-h-11',
  minWidth: 'min-w-11',
} as const;

/**
 * Standard Spacing (8px Grid)
 */
export const SPACING = {
  xs: 'gap-1',   // 4px
  sm: 'gap-2',   // 8px
  md: 'gap-3',   // 12px
  lg: 'gap-4',   // 16px
  xl: 'gap-6',   // 24px
} as const;

/**
 * Z-Index Stacking
 */
export const Z_INDEX = {
  dropdown: 'z-10',
  sticky: 'z-20',
  modal: 'z-40',
  toast: 'z-50',
  tooltip: 'z-[60]',
} as const;
